// Hidden developer screen: the Curriculum Alignment export. Reached via a long-press
// on the version number in the parent dashboard. Deliberately utilitarian (monospace,
// no game chrome) — this is a document for a curriculum committee, not a player.
// Generates the full report as plain text or JSON, with copy + download.

import { useMemo, useState } from 'react';
import { buildCurriculumReport, renderReportText, reportJSON } from './curriculumReport.js';

const C = {
  bg: '#0f1115',
  panel: '#171a21',
  border: '#2a2f3a',
  ink: '#e6e9ef',
  ink2: '#9aa4b1',
  accent: '#4ea1ff',
};
const MONO = "'SF Mono', ui-monospace, 'Cascadia Code', Consolas, monospace";

export function CurriculumScreen({ onClose }: { onClose: () => void }) {
  const [format, setFormat] = useState<'text' | 'json'>('text');
  const [copied, setCopied] = useState(false);

  // Stamp the generation time once per open (Date is fine in the browser runtime).
  const generatedAt = useMemo(() => new Date().toISOString(), []);
  const report = useMemo(() => buildCurriculumReport(generatedAt), [generatedAt]);
  const text = useMemo(() => renderReportText(report), [report]);
  const json = useMemo(() => reportJSON(report), [report]);
  const body = format === 'text' ? text : json;

  const counts = report.summary.counts;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const download = () => {
    const blob = new Blob([body], { type: format === 'json' ? 'application/json' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bizkid-curriculum-alignment.${format === 'json' ? 'json' : 'txt'}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const tab = (id: 'text' | 'json', label: string) => (
    <button
      type="button"
      onClick={() => setFormat(id)}
      style={{
        padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: MONO, fontSize: 12.5,
        border: `1px solid ${format === id ? C.accent : C.border}`,
        background: format === id ? 'rgba(78,161,255,0.15)' : 'transparent',
        color: format === id ? C.accent : C.ink2, fontWeight: 600,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: C.bg, color: C.ink, fontFamily: MONO, display: 'flex', flexDirection: 'column' }}>
      {/* toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 13.5 }}>📋 Curriculum Alignment Export</span>
        <span style={{ fontSize: 11.5, color: C.ink2 }}>
          {counts.mechanics} mechanics · {counts.decisionCards} cards / {counts.decisionLessons} lessons · {counts.mentorLines} mentor lines · {counts.uniqueLearningMoments} learning moments
        </span>
        <span style={{ flex: 1 }} />
        {tab('text', 'TEXT')}
        {tab('json', 'JSON')}
        <button type="button" onClick={copy} style={btn(C)}>{copied ? '✓ Copied' : 'Copy'}</button>
        <button type="button" onClick={download} style={btn(C)}>Download</button>
        <button type="button" onClick={onClose} style={{ ...btn(C), borderColor: C.border }}>✕ Close</button>
      </div>

      {/* report body */}
      <pre
        style={{
          margin: 0, flex: 1, overflow: 'auto', padding: '16px 18px',
          fontFamily: MONO, fontSize: 12, lineHeight: 1.5, color: C.ink,
          whiteSpace: 'pre', tabSize: 2,
        }}
      >
        {body}
      </pre>
    </div>
  );
}

function btn(C: { accent: string; border: string; ink: string }): React.CSSProperties {
  return {
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: MONO, fontSize: 12.5,
    border: `1px solid ${C.border}`, background: 'transparent', color: C.ink, fontWeight: 600,
  };
}
